import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  Box,
} from '@mui/material';
import { post, put } from 'src/utils/networkUtils';

// ----------------------------------------------------------------------

SeasonDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  season: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default function SeasonDialog({ open, season, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    seasonYear: '',
    displayName: '',
    isVisible: false,
    isCurrentSeason: false,
  });
  const [loading, setLoading] = useState(false);

  const isEditMode = Boolean(season);

  useEffect(() => {
    if (season) {
      setFormData({
        seasonYear: season.seasonYear || '',
        displayName: season.displayName || '',
        isVisible: Boolean(season.isVisible),
        isCurrentSeason: Boolean(season.isCurrentSeason),
      });
    } else {
      setFormData({
        seasonYear: '',
        displayName: '',
        isVisible: false,
        isCurrentSeason: false,
      });
    }
  }, [season]);

  const handleChange = (field) => (event) => {
    let value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    
    // Convert seasonYear to number
    if (field === 'seasonYear' && value !== '') {
      value = parseInt(value, 10) || '';
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (isEditMode) {
        // Edit existing season
        await put(`/admin/seasons/${season.seasonId}`, formData);
      } else {
        // Create new season - exclude isCurrentSeason
        const { isCurrentSeason, ...createData } = formData;
        await post(`/admin/seasons`, createData);
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving season:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const isFormValid = formData.seasonYear && String(formData.seasonYear).trim() && formData.displayName && formData.displayName.trim();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {isEditMode ? 'Edit Season' : 'Create Season'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Season Year"
                type="number"
                value={formData.seasonYear}
                onChange={handleChange('seasonYear')}
                fullWidth
                required
                disabled={loading}
                placeholder="e.g., 2024"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Display Name"
                value={formData.displayName}
                onChange={handleChange('displayName')}
                fullWidth
                required
                disabled={loading}
                placeholder="e.g., 2024 Season"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isCurrentSeason}
                    onChange={handleChange('isCurrentSeason')}
                    disabled={loading || !isEditMode}
                  />
                }
                label="Current Season"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isVisible}
                    onChange={handleChange('isVisible')}
                    disabled={loading}
                  />
                }
                label="Visible"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !isFormValid}
        >
          {loading ? 'Saving...' : (isEditMode ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
